/**
 * ImagePreviewModal - Full-screen modal for viewing images
 */

import { useEffect, useCallback } from 'react'
import { X, Download } from 'lucide-react'
import clsx from 'clsx'
import { useTranslation } from '@/i18n'

interface ImagePreviewModalProps {
  imageUrl: string
  alt?: string
  onClose: () => void
}

export function ImagePreviewModal({ imageUrl, alt, onClose }: ImagePreviewModalProps) {
  const { t } = useTranslation()
  const resolvedAlt = alt ?? t('chatv2:image.preview_alt', { defaultValue: 'Image preview' })
  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = ''
    }
  }, [])

  const handleDownload = useCallback(async () => {
    try {
      const response = await fetch(imageUrl)
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = resolvedAlt || 'image'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to download image:', error)
    }
  }, [imageUrl, resolvedAlt])

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      {/* Toolbar */}
      <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
        <button
          onClick={(e) => {
            e.stopPropagation()
            handleDownload()
          }}
          className={clsx(
            'p-2 rounded-full',
            'bg-white/10 hover:bg-white/20',
            'text-white transition-colors'
          )}
          title={t('chatv2:image.download', { defaultValue: 'Download image' })}
        >
          <Download className="h-5 w-5" />
        </button>
        <button
          onClick={onClose}
          className={clsx(
            'p-2 rounded-full',
            'bg-white/10 hover:bg-white/20',
            'text-white transition-colors'
          )}
          title={t('chatv2:image.close', { defaultValue: 'Close preview' })}
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Image container */}
      <div
        className="max-w-[90vw] max-h-[90vh] p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={imageUrl}
          alt={resolvedAlt}
          className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
        />
      </div>

      {/* Caption */}
      {alt && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-black/50 rounded-lg">
          <p className="text-white text-sm">{alt}</p>
        </div>
      )}
    </div>
  )
}

export default ImagePreviewModal
